import React, { useState } from "react";
import Api from "../axiosConfig";
import { Link } from "react-router-dom";

const OptInPage = () => {
  const [formData, setFormData] = useState({
    phone: "",
    company: "",
    consent: {
      sms: false,
      email: false,
      marketing: false,
    },
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleConsentChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      consent: {
        ...prev.consent,
        [name]: checked,
      },
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Name and email removed

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\+?[1-9]\d{1,14}$/.test(formData.phone.replace(/\s/g, ""))) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (
      !formData.consent.sms &&
      !formData.consent.email &&
      !formData.consent.marketing
    ) {
      newErrors.consent = "Please select at least one communication preference";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await Api.post("/v1/optform", formData);

      if (response.data.success) {
        setSuccess(true);
        // Reset form
        setFormData({
          phone: "",
          company: "",
          consent: {
            sms: false,
            email: false,
            marketing: false,
          },
        });
      }
    } catch (error) {
      console.error("Opt-in form error:", error);

      if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: "An error occurred. Please try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center space-y-6">
            <div className="w-16 h-16 mx-auto bg-green-50 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold text-gray-900">
                All set!
              </h2>
              <p className="text-gray-600">
                You'll receive updates based on your preferences.
              </p>
            </div>

            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
            >
              Continue
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-semibold text-gray-900">
            Stay updated
          </h1>
          <p className="text-gray-600">
            Get notified about delivery updates and services
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Phone number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 transition-colors ${
                  errors.phone 
                    ? "border-red-300 focus:ring-red-500" 
                    : "border-gray-300"
                }`}
                placeholder="Enter your phone number"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="company"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Company
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 transition-colors"
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-900">
              How would you like to be contacted? *
            </label>
            
            <div className="space-y-3">
              <label className="flex items-start space-x-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="sms"
                  checked={formData.consent.sms}
                  onChange={handleConsentChange}
                  className="mt-0.5 w-5 h-5 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                />
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-900">
                    SMS updates
                  </div>
                  <div className="text-sm text-gray-600">
                    Delivery notifications and updates
                  </div>
                </div>
              </label>

              <label className="flex items-start space-x-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="email"
                  checked={formData.consent.email}
                  onChange={handleConsentChange}
                  className="mt-0.5 w-5 h-5 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                />
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-900">
                    Email updates
                  </div>
                  <div className="text-sm text-gray-600">
                    Confirmations and status updates
                  </div>
                </div>
              </label>

              <label className="flex items-start space-x-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="marketing"
                  checked={formData.consent.marketing}
                  onChange={handleConsentChange}
                  className="mt-0.5 w-5 h-5 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                />
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-900">
                    Service updates
                  </div>
                  <div className="text-sm text-gray-600">
                    New features and promotions
                  </div>
                </div>
              </label>
            </div>
            
            {errors.consent && (
              <p className="text-sm text-red-600">{errors.consent}</p>
            )}
          </div>

          {errors.general && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.general}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              loading
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-gray-900 text-white hover:bg-gray-800"
            }`}
          >
            {loading ? "Subscribing..." : "Subscribe"}
          </button>

          <div className="text-center space-y-4">
            <p className="text-xs text-gray-500 leading-relaxed">
              You can unsubscribe at any time. Message and data rates may apply.
            </p>
            
            <Link
              to="/"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to home
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OptInPage;